import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormlyInputType, GaFormlyService, IExtraFieldConfigurationObject } from '../../../shared/formly/ga-formly.service';
import { IObject } from '../../../../type_definitions/datatypes';
import { UntypedFormGroup } from '@angular/forms';
import { IReportMetadata } from '../../../shared/report/report-data.service';
import { GaValidateService } from '../../../shared/formly/ga-validate.service';
import * as _ from 'underscore';
import { IStoredReportBusy, STORED_REPORT_ADD_MODE, STORED_REPORT_EDIT_MODE } from '../report.component';
import { ReportFiltersUtil } from '../../../shared/report/report-filters-util.service';
import { IStoredReport } from '../../../shared/report/stored-report-data.service';
import { AuthService } from '../../../shared/services/auth/auth.service';

export interface IStoredReportDialogParamEvent {
  filters?: any;
  model?: IStoredReportDialogModel;
}

export interface IStoredReportDialogModel {
  report?: IStoredReport;
  reportName?: string;
  category?:string;
  sequence?:number;
}

@Component({
  selector: 'stored-report-dialog',
  template: `
    <div class="modal-header">
      <h4 class="modal-title">{{mode === '${STORED_REPORT_ADD_MODE}' ? ('REPORTS.SAVE_NEW_REPORT' | translate) : ('REPORTS.EDIT_MY_REPORT' | translate)}}</h4>
    </div>
    <div class="modal-body">
      <div>
        <div class="row">
          <div class="col-sm-12 col-space-bottom" *ngIf="storedReport?.sharedStoredReport">
            <div class="alert alert-warning">
              <i class="fa fa fa-exclamation-triangle margin-right-xs"></i>
              <span translate>REPORTS.SHARED_STORED_REPORT.WARNING</span>
            </div>
          </div>
        </div>
        <formly [fields]="fields" [model]="model" [form]="form"></formly>
        <hr/>
        <report-filters [reportMetadata]="metadata" [model]="filters" [showFilters]="true" valueClass="col-xsm-9 col-sm-9 col-md-8 col-lg-8" class="clearfix" (onFilterChange)="onFilterChange($event)"></report-filters>
      </div>
    </div>
    <div class="modal-footer">
      <button
        class="btn btn-success"
        [busyButton]="busy.saving"
        [disabled]="busy.deleting"
        (click)="saveStoredReport()">
        {{mode === '${STORED_REPORT_ADD_MODE}' ? ('GA.BUTTONS.SAVE_LABEL' | translate: { label: 'new report' }) : ('GA.BUTTONS.SAVE' | translate)}}
      </button>
      <button
        class="btn btn-danger"
        [busyButton]="busy.deleting"
        [disabled]="busy.saving"
        *ngIf="mode === '${STORED_REPORT_EDIT_MODE}'"
        (click)="onDeleteStoredReport.emit({ model: model })" translate>
        GA.BUTTONS.DELETE
      </button>
      <button
        class="btn btn-default"
        [disabled]="busy.saving || busy.deleting"
        (click)="onDialogClose.emit(filters)" translate>
        GA.BUTTONS.CANCEL
      </button>
    </div>
  `
})
export class StoredReportDialogComponent implements OnInit, OnChanges {
  @Input() public busy: IStoredReportBusy;
  @Input() public mode: string;
  @Input() public storedReports: IStoredReport[];
  @Input() public storedReport: IStoredReport;
  @Input() public metadata: IReportMetadata;
  @Input() public filters: IObject;

  @Output() public onSaveStoredReport: EventEmitter<IStoredReportDialogParamEvent> = new EventEmitter<IStoredReportDialogParamEvent>();
  @Output() public onDeleteStoredReport: EventEmitter<IStoredReportDialogParamEvent> = new EventEmitter<IStoredReportDialogParamEvent>();
  @Output() public onDialogClose: EventEmitter<IObject> = new EventEmitter<IObject>();
  
  public fields: IExtraFieldConfigurationObject[];
  public model: IStoredReportDialogModel = {};
  public form: UntypedFormGroup = new UntypedFormGroup({});

  private reportNameField:IExtraFieldConfigurationObject;
  private selectReportField:IExtraFieldConfigurationObject;
  private newButtonCustomField:IExtraFieldConfigurationObject;
  private isACSLUser:boolean;

  constructor(
    private formlyService: GaFormlyService,
    private reportFiltersUtil: ReportFiltersUtil,
    private validateService: GaValidateService,
    private authService:AuthService
  ) {
    /**/
  }

  public ngOnInit(): void {
    this.isACSLUser = this.authService.checkAcslUser();

    this.model = {
      report: this.storedReport,
      reportName: this.storedReport?.description,
      category: this.storedReport?.category,
      sequence: this.storedReport?.sequence
    };

    this.fields = this.createFields();
    this.updateFieldVisibility();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.storedReports) {
      this.storedReports = _.map(changes.storedReports.currentValue, (storedReport: IStoredReport) => {
        if (storedReport.editDisabled) {
          // disable formly option in dialog because this is usually stored report that coming from server not from user's list
          (<any>storedReport).disabled = true;
        }

        return storedReport;
      });
    }
  }

  public saveStoredReport(): void {
    this.validateService.validateFields(this.fields, this.model, true)
      .then(() => {
        this.onSaveStoredReport.emit({ model: this.model, filters: this.filters });
      });
  }

  public onFilterChange(filterValue: IObject): void {
    this.filters = filterValue;
  }

  private createFields(): IExtraFieldConfigurationObject[] {
    const labelClass:string = 'col-sm-2';
    const valueClass:string = 'col-sm-10';
    const fullWidthClass:string = 'col-sm-12';
    const halfWidthClass:string = 'col-sm-6';

    return [
      this.reportNameField = this.formlyService.anInput('reportName', 'Report name')
        .withRequired(true)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withWrapperClass(fullWidthClass)
        .build(),
      this.selectReportField = this.formlyService.aSelect('report', 'Report')
        .withRequired(true)
        .withOptions(this.storedReports)
        .withFormat((sr:IStoredReport) => sr.sharedStoredReport ? `[Shared] ${sr.description}` : sr.description)
        .onChange(this.onStoredReportChange)
        .withLabelClass('col-sm-4')
        .withValueClass('col-sm-8')
        .withWrapperClass(halfWidthClass)
        .build(),
      this.newButtonCustomField = this.formlyService.aCustom()
        .withComponent(StoredReportDialogNewButtonComponent)
        .withLabelClass('d-none')
        .withWrapperClass(halfWidthClass)
        .withExtraOptions({
          templateOptions: {
            inputs: {
              saving: this.busy.saving
            },
            outputs: {
              onCreateNew: this.onCreateNew
            }
          }
        })
        .build(),
      this.formlyService.anInput('category', 'Category')
        .withLabelClass(labelClass)
        .withValueClass('col-sm-4')
        .withWrapperClass(fullWidthClass)
        .withInfo('Category will be used for grouping stored reports')
        .showInfo()
        .withMaxLength(50)
        .withHideExpression(() => !this.isACSLUser)
        .build(),
      this.formlyService.anInput('sequence', 'Sequence')
        .withInputType(FormlyInputType.NUMBER)
        .withLabelClass(labelClass)
        .withValueClass('col-sm-2')
        .withWrapperClass(fullWidthClass)
        .withHideExpression(() => !this.isACSLUser)
        .withExtraOptions({
          templateOptions: {
            min: 0
          }
        })
        .build()
    ];
  }

  private onCreateNew = (): void => {
    this.mode = STORED_REPORT_ADD_MODE;
    this.updateFieldVisibility();
  };
  
  private updateFieldVisibility(): void {
    this.reportNameField.hide = this.mode !== STORED_REPORT_ADD_MODE;
    this.selectReportField.hide = this.mode === STORED_REPORT_ADD_MODE;
    this.newButtonCustomField.hide = this.mode === STORED_REPORT_ADD_MODE;
  }
  
  private onStoredReportChange = (storedReport: IStoredReport): void => {
    this.form.patchValue({ category: storedReport?.category, sequence: storedReport?.sequence });
    // set to model directly because field is hidden
    this.model.reportName = this.model.reportName || storedReport?.description;
    this.filters = this.reportFiltersUtil.convertStoredReportToFilters(this.metadata, storedReport);
  };
}

@Component({
  selector: 'stored-report-dialog-new',
  template: `
    <button class="btn btn-default" [disabled]="saving" (click)="onCreateNew.emit()"><i class="fa fa-plus"></i> New</button>
  `
})
export class StoredReportDialogNewButtonComponent {
  @Input() public saving: boolean;
  @Output() public onCreateNew: EventEmitter<void> = new EventEmitter<void>();
}